<template>
    <div class="column is-narrow schedule-mat-column schedule-dropzone-column">
        <div class="is-unselectable schedule-mat-header drop-zone">
            <span class="has-text-weight-bold"
                >{{ translate("admin_schedule_manage.unscheduled_categories") }} ({{ filteredDropZoneItems.length }})</span
            >
            <span
                v-if="showScheduleStore.selectedItems.size > 0 && showScheduleStore.activeSelectionContext?.type === 'dropzone'"
                class="tag is-info is-light ml-2"
            >
                {{ showScheduleStore.selectedItems.size }} {{ translate("admin_schedule_manage.selected") }}
                <button class="delete is-small" @click="clearSelection"></button>
            </span>
        </div>

        <div class="schedule-mat-content drop-zone" :class="{ 'has-pointer-grab': showScheduleStore.editMode }" data-dragscroll>
            <Sortable
                :key="sortableVersion"
                :options="dragOptions"
                :list="filteredDropZoneItems"
                item-key="key"
                @start="showScheduleStore.isDraggingCategory = true"
                @end="showScheduleStore.isDraggingCategory = false"
                @add="onAddedToMat($event)"
                @remove="onRemovedFromMat($event)"
                style="position: relative; z-index: 1; flex: 1"
            >
                <template v-slot:item="{ element: dropZoneItem }">
                    <div
                        class="box is-condensed mat-schedule-item dropzone-sliced-item"
                        :class="{ 'is-selected': isSelected(dropZoneItem.key), 'is-hoverable': showScheduleStore.editMode }"
                        @click.stop="toggleSelection(dropZoneItem.key, $event)"
                    >
                        <div class="is-flex is-justify-content-space-between is-align-items-flex-start">
                            <span class="dropzone-item-name">{{ dropZoneItem.name }}</span>
                            <span class="is-text-wrap-nowrap">
                                <span class="icon">
                                    <i class="fas fa-users"></i>
                                </span>
                                <span>{{ dropZoneItem.totalRealCompetitors }}</span>
                            </span>
                        </div>
                        <div v-if="hasAnyLabel(dropZoneItem)" class="dropzone-coverage mt-1">
                            <ScheduleSegmentTag
                                :from="dropZoneItem.segment.from"
                                :to="dropZoneItem.segment.to"
                                :includes-third-place="dropZoneItem.segment.includesThirdPlace"
                                :assignment-type="dropZoneItem.assignmentType"
                            />
                        </div>
                    </div>
                </template>
            </Sortable>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { Sortable } from "sortablejs-vue3";
    import { useShowScheduleStore } from "./useShowScheduleStore";
    import { useScheduleMainStore } from "./useScheduleMainStore";
    import { computed, ref } from "vue";
    import schedulesApi from "../../Api/schedules";
    import { handleError } from "../../Common/errorHandler";
    import objectMatcher from "../../Common/objectMatcher";
    import { translate } from "../../Common/translations";
    import { SortableEvent } from "../../Common/sortableUtils";
    import { DropZoneItem, getRoundMarkerTranslationKey } from "./dropZoneItems";
    import ScheduleSegmentTag from "../ScheduleSegmentTag.vue";

    const emit = defineEmits<{
        categoryScheduled: [matIds: number[]];
        categoryUnscheduled: [matIds: number[]];
    }>();

    const showScheduleStore = useShowScheduleStore();
    const mainStore = useScheduleMainStore();

    const sortableVersion = ref(0);

    const dragOptions = computed(() => ({
        group: "matSchedule",
        animation: 150,
        scroll: true,
        sort: false,
        forceFallback: true,
        bubbleScroll: true,
        emptyInsertThreshold: 100,
        disabled: !showScheduleStore.editMode,
    }));

    // Selection methods
    function isSelected(key: string): boolean {
        return showScheduleStore.isSelected(key);
    }

    function toggleSelection(key: string, event: MouseEvent) {
        if (!showScheduleStore.editMode) {
            return;
        }

        // Set active context to dropzone (clears selection if coming from a mat)
        showScheduleStore.setActiveContext("dropzone");

        // SHIFT+click: range selection
        if (event.shiftKey) {
            selectRangeFromClosestSelected(key);
            return;
        }

        // CTRL+click (or CMD+click on Mac): toggle individual selection
        if (event.ctrlKey || event.metaKey) {
            showScheduleStore.toggleSingleSelection(key);
            return;
        }

        // Regular click: clear selection and select only this item
        showScheduleStore.setSingleSelection(key);
    }

    function selectRangeFromClosestSelected(targetKey: string) {
        const list = filteredDropZoneItems.value;
        const targetIndex = list.findIndex(item => item.key === targetKey);

        // Handle case where target not found
        if (targetIndex === -1) {
            return;
        }

        // If no items selected, just select the clicked item
        if (showScheduleStore.selectedItems.size === 0) {
            showScheduleStore.setSingleSelection(targetKey);
            return;
        }

        // Find all currently selected indices
        const selectedIndices = list
            .map((item, index) => ({ id: item.key, index }))
            .filter(item => showScheduleStore.isSelected(item.id))
            .map(item => item.index);

        if (selectedIndices.length === 0) {
            showScheduleStore.setSingleSelection(targetKey);
            return;
        }

        // Find the closest selected index to the target
        const closestIndex = selectedIndices.reduce((closest, current) => {
            const currentDistance = Math.abs(current - targetIndex);
            const closestDistance = Math.abs(closest - targetIndex);
            return currentDistance < closestDistance ? current : closest;
        });

        // Select range from closest selected item to target
        const minIndex = Math.min(closestIndex, targetIndex);
        const maxIndex = Math.max(closestIndex, targetIndex);

        const itemsToSelect: string[] = [];
        for (let i = minIndex; i <= maxIndex; i++) {
            itemsToSelect.push(list[i].key);
        }

        if (closestIndex > targetIndex) {
            // Reverse order if selecting upwards, for keeping the same order of selection that user expects.
            itemsToSelect.reverse();
        }

        showScheduleStore.selectRange(itemsToSelect);
    }

    function clearSelection() {
        showScheduleStore.clearSelection();
    }

    function getSelectedDropZoneItems(): DropZoneItem[] {
        return filteredDropZoneItems.value.filter(item => showScheduleStore.isSelected(item.key));
    }

    /**
     * Handles the @add event when a scheduled category is dragged back from a mat to DropZone (unscheduling).
     */
    function onAddedToMat(event: SortableEvent) {
        showScheduleStore.dropZoneAdded = { newIndex: event.newIndex };

        return handleMatToDropZoneMove().finally(() => {
            sortableVersion.value++;
        });
    }

    /**
     * Handles the @remove event when a category is dragged from DropZone to a mat (scheduling).
     * If items are selected, drag all selected items. Otherwise, drag only the dragged item.
     */
    function onRemovedFromMat(event: SortableEvent) {
        const draggedItem = filteredDropZoneItems.value[event.oldIndex];
        let itemsToMove: DropZoneItem[] = [];

        // If there are selected items and the dragged item is among them, move all selected items
        if (showScheduleStore.selectedItems.size > 0 && showScheduleStore.isSelected(draggedItem.key)) {
            itemsToMove = getSelectedDropZoneItems();
        } else {
            // Otherwise, just move the single dragged item
            itemsToMove = [draggedItem];
        }

        showScheduleStore.dropZoneRemoved = {
            dropZoneItems: itemsToMove,
            oldIndex: event.oldIndex,
        };

        return handleDropZoneToMatMove().finally(() => {
            sortableVersion.value++;
        });
    }

    /**
     * Handles dragging a category (or multiple selected categories) from DropZone to a mat (scheduling).
     *
     * Logic: Waits for both @remove (from DropZone) and @add (to mat) events to fire,
     * then calls the API to create the scheduled categories with assignmentType=1 (entire bracket).
     * Passes the target position to insert the categories at the exact drop location.
     */
    function handleDropZoneToMatMove(): Promise<void> {
        const dropZoneRemoved = showScheduleStore.dropZoneRemoved;
        const matAdded = showScheduleStore.matAdded;

        // Wait for both events
        if (!dropZoneRemoved.dropZoneItems || dropZoneRemoved.dropZoneItems.length === 0 || matAdded.matId === 0) {
            return Promise.resolve();
        }

        // Clear state immediately to avoid double request
        const targetMatId = matAdded.matId;
        const dropZoneItems = dropZoneRemoved.dropZoneItems;
        const targetPosition = matAdded.newIndex!;

        //Calculate position on the target including blocked and movable categories
        const blocked = showScheduleStore.blockedScheduledCategories[targetMatId] ?? [];
        //Target position is among the movable only, we need to recalculate to include blocked offset
        const targetPositionWithBlocked = targetPosition + blocked.length;

        showScheduleStore.dropZoneRemoved = { dropZoneItems: [], oldIndex: null };
        showScheduleStore.matAdded = { matId: 0, newIndex: null };

        return saveDropZoneItemsSequentially(dropZoneItems, targetMatId, targetPositionWithBlocked)
            .then(() => {
                clearSelection();
                emit("categoryScheduled", [targetMatId]);
                return mainStore.refreshDropZoneItems();
            })
            .catch(err => {
                showScheduleStore.pendingMatScroll = null;
                handleError(err);
                return mainStore.refreshDropZoneItems();
            });
    }

    function saveDropZoneItemsSequentially(dropZoneItems: DropZoneItem[], targetMatId: number, targetPosition: number): Promise<void> {
        return dropZoneItems.reduce((promise, item, index) => {
            return promise.then(() =>
                mainStore.trackRequest(
                    schedulesApi.bulkSaveScheduledCategories({
                        eventId: mainStore.eventId,
                        scheduleId: mainStore.scheduleId,
                        matIds: [targetMatId],
                        parameterIds: [item.parameterId],
                        assignmentType: item.assignmentType,
                        addingOrder: "selection",
                        targetPosition: targetPosition + index,
                        roundMarkerFrom: item.roundMarkerFrom,
                        roundMarkerTo: item.roundMarkerTo,
                    }),
                ),
            );
        }, Promise.resolve());
    }

    function hasAnyLabel(dropZoneItem: DropZoneItem): boolean {
        return dropZoneItem.segment.from !== null || dropZoneItem.segment.to !== null || dropZoneItem.assignmentType > 1;
    }

    /**
     * Handles dragging one or more categories from a mat back to DropZone (unscheduling).
     *
     * Logic: Waits for both @remove (from mat) and @add (to DropZone) events to fire,
     * then calls the API to delete the scheduled category/categories.
     * Note: This is now handled primarily by SingleMatSchedule component.
     */
    function handleMatToDropZoneMove() {
        const matRemoved = showScheduleStore.matRemoved;
        const dropZoneAdded = showScheduleStore.dropZoneAdded;

        // Wait for both events
        if (matRemoved.matId === 0 || dropZoneAdded.newIndex === null) {
            return Promise.resolve();
        }

        // Clear state immediately to avoid double request
        const scheduledCategoryIds = matRemoved.scheduledCategoryIds;

        showScheduleStore.matRemoved = { matId: 0, oldIndex: null, scheduledCategoryIds: [] };

        return showScheduleStore
            .removeScheduledCategories(scheduledCategoryIds)
            .catch(handleError)
            .finally(() => {
                //Delayed to prevent reorder handler in source mat.
                showScheduleStore.dropZoneAdded = { newIndex: null };
            });
    }

    const dropZoneItems = computed(() => mainStore.dropZoneItems);
    const filteredDropZoneItems = computed(() => dropZoneItems.value.filter(matchesSearchQuery));

    function matchesSearchQuery(dropZoneItem: DropZoneItem): boolean {
        const searchQuery = showScheduleStore.scheduledCategorySearchQuery;
        if (searchQuery.trim() === "") {
            return true;
        }

        const segmentLabels = [dropZoneItem.segment.from, dropZoneItem.segment.to]
            .filter((marker): marker is NonNullable<typeof marker> => marker !== null)
            .map(marker => translate(getRoundMarkerTranslationKey(marker)) ?? marker);

        return objectMatcher.matchString(searchQuery, [dropZoneItem.name, ...segmentLabels].join(" "));
    }
</script>

<style scoped>
    .ml-2 {
        margin-left: 0.5rem;
    }

    .dropzone-sliced-item {
        display: block;
    }

    .dropzone-coverage {
        line-height: 1.3;
    }
</style>
